export default function WinterGardenDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Winter Garden requests are family travel with under 4 weeks notice",
    },
    {
      value: "3–4 weeks",
      label: "average notice for Caribbean cruise and Mexico resort bookings",
    },
    {
      value: "Feb–Apr",
      label: "peak passport season — spring break cruise and summer Europe bookings",
    },
    {
      value: "48-hour",
      label: "shortest departure window we've accommodated for a Winter Garden family",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common destination for Winter Garden families — whether by Disney Cruise Line from Port Canaveral (only 55 miles east) or by direct flight from MCO to Nassau, Jamaica, or the Dominican Republic. The Bahamas is particularly popular for 3- and 4-night Disney cruises that depart multiple times per week. All cruise passengers and most air travelers need valid passports, including children.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Riviera Maya",
      context: "Cancun, Playa del Carmen, and Los Cabos are staple destinations for Winter Garden families seeking beach vacations within a short, direct flight from MCO. Spring break bookings surge in January with departures in March. Mexico requires six months of passport validity for entry, a rule that catches many travelers who assume their passport is valid until the printed expiration date.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February for Winter Garden families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London is a major destination for Winter Garden medical professionals attending conferences and for families exploring European history and culture. Direct flights from MCO to London Gatwick and Manchester make the UK more accessible than ever. Ireland draws heritage travelers and golf enthusiasts from the western Orange County area.",
      urgency: "Conference invitations often arrive 2-4 weeks before the event — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France, Italy & Spain",
      context: "Paris, Rome, and Barcelona are the top European cultural destinations for Winter Garden families and students. UCF and Valencia College study abroad programs in Spain and Italy create a steady stream of student passport needs each spring and fall. Summer family vacations to these cities typically require passports with at least six months of remaining validity.",
      urgency: "Study abroad acceptance letters arrive 4-6 weeks before departure — tight window for first-time applicants",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica & Central America",
      context: "Costa Rica has become increasingly popular with Winter Garden families seeking eco-tourism and adventure travel — zip-lining, volcano hikes, and wildlife encounters that offer a different experience than the typical beach vacation. Direct flights from MCO to San Jose make this an easy long-weekend or spring break destination. Costa Rica requires six months of passport validity.",
      urgency: "Adventure travel bookings often happen 2-3 weeks before departure — shorter planning window than traditional vacations",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Montreal, and Vancouver attract Winter Garden travelers for business conferences, hockey tournaments, summer cultural festivals, and Niagara Falls family trips. While Canada does not strictly enforce the six-month rule for U.S. citizens, airlines may deny boarding if your passport expires within a few months of your return date. Direct flights from MCO to Toronto operate daily.",
      urgency: "Business trips to Toronto and Montreal often booked 1-3 weeks out — tight for occasional travelers with expired passports",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Winter Garden Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Winter Garden
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Winter Garden travelers head to the Caribbean for Disney cruises, Mexico for spring break, Europe for summer vacations and conferences, and Central America for adventure. These are the destinations that create the most passport urgency for our Winter Garden clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Orlando", href: "/orlando" },
              { name: "Winter Park", href: "/winter-park" },
              { name: "Windermere", href: "/windermere" },
              { name: "Tampa", href: "/tampa" },
              { name: "Lakeland", href: "/tampa" },
              { name: "Clearwater", href: "/clearwater" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}